import { Component } from '@angular/core';
import { AuthService } from '../../../services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ReportService } from '../../../services/report.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Report } from '../../../models/report.model';
import { IReportsResponse } from '../../../models/reports-response.model';
import { CreateReportModalComponent } from '../../../components/create-report-modal/create-report-modal.component';
import { ReportStatusVariant } from '../report-list/report-list.component';
import { ReportModalComponent } from '../../../components/report-modal/report-modal.component';
import { ToastService } from '../../../services/toast.service';

/** Llave de cada bandeja dentro de `reportService.reports`. La toma del modelo
    de la respuesta para que agregar una bandeja en la API no deje pestañas
    silenciosamente desactualizadas. */
export type ReportTabKey = keyof IReportsResponse;

/** Pestaña del selector de estado, ya resuelta para el template. */
export interface IReportTab {
  key: ReportTabKey;
  title: string;
  icon: string;
  /** Sufijo BEM del color semántico, el mismo que usa el chip de la tabla. */
  variant: ReportStatusVariant;
  emptyMessage: string;
}

@Component({
  selector: 'app-reports-page',
  templateUrl: './reports-page.component.html',
  styleUrl: './reports-page.component.scss'
})
export class ReportsPageComponent {

  public neighborhoodId?: string;

  /** Bandeja visible. Pendientes primero: es la que exige acción. */
  public activeTab: ReportTabKey = 'pendings';

  /**
   * Reporte que un aviso de la campana pidió abrir (`?reportId=`).
   *
   * Se guarda en vez de abrirse de inmediato porque el aviso trae el id pero no
   * dice en qué bandeja vive el reporte, y las bandejas todavía pueden estar
   * cargando. Se resuelve contra las tres listas ya cargadas: es el único lugar
   * donde ese dato existe.
   */
  private pendingReportId?: string;

  /** Orden fijo del flujo de un reporte: se abre, se atiende, se cierra. */
  public readonly tabs: IReportTab[] = [
    {
      key: 'pendings',
      title: 'Pendientes',
      icon: 'pending_actions',
      variant: 'Pending',
      emptyMessage: 'No hay reportes pendientes. Todo lo que llega aparece aquí primero.'
    },
    {
      key: 'inProgress',
      title: 'En progreso',
      icon: 'autorenew',
      variant: 'Progress',
      emptyMessage: 'No hay reportes en progreso. Pasa un pendiente a progreso cuando empieces a atenderlo.'
    },
    {
      key: 'resolved',
      title: 'Resueltos',
      icon: 'task_alt',
      variant: 'Resolved',
      emptyMessage: 'Todavía no hay reportes resueltos.'
    },
    {
      /* Los cancelados van al final: son el cierre que NO resolvió nada. Un
         reporte cancelado conserva su status, por eso vive en su propia bandeja
         y no se mezcla con la del estado que traía. */
      key: 'canceled',
      title: 'Cancelados',
      icon: 'cancel',
      variant: 'Canceled',
      emptyMessage: 'No hay reportes cancelados. Aquí quedan los que se dieron de baja, con el motivo de quien los canceló.'
    }
  ];

  constructor(
    public authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private modalService: NgbModal,
    public reportService: ReportService,
    private router: Router,
    private toastService: ToastService
  ) {
    /* Colonia y pestaña viajan en la misma fuente: `neighborhoodId` lo hereda
       la ruta padre y `tab` es el segmento propio de esta ruta. Cambiar de
       pestaña vuelve a emitir `params`, por eso la pestaña se resuelve primero
       y la carga queda detrás de su propia guarda: los reportes dependen solo
       de la colonia y se piden una sola vez por colonia. */
    this.activatedRoute.params.subscribe(
      async params => {
        /* Un segmento ausente o con basura no rompe la página: cae en la
           bandeja por defecto. Ignorar el valor repetido evita el bucle con
           `selectTab()`, que además ya sale temprano si la pestaña no cambia. */
        const tab: ReportTabKey = this.resolveTab(
          params['tab']
        ) ?? 'pendings';

        if (tab !== this.activeTab) {
          this.activeTab = tab;
        }

        const neighborhoodId: string = params['neighborhoodId'];

        if (this.neighborhoodId === neighborhoodId) {
          /* Ya cargado: si venimos de un aviso el reporte se puede abrir ya. */
          this.openPendingReport();
          return;
        }

        this.neighborhoodId = neighborhoodId;
        await this.reportService.getAll(
          this.neighborhoodId!
        );

        /* Las tres bandejas ya existen: aquí sí se puede saber en cuál vive el
           reporte que pidió el aviso. */
        this.openPendingReport();

      }
    );

    /* El `reportId` viaja como query param y no como estado de navegación para
       que recargar o compartir la URL siga funcionando. Los dos flujos —query
       primero o carga primero— terminan llamando al mismo método, que se
       protege solo. */
    this.activatedRoute.queryParams.subscribe(
      params => {

        const reportId: string | undefined = params['reportId'];

        if (!reportId) return;

        this.pendingReportId = reportId;

        this.openPendingReport();

      }
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR resolveTab                            */
  /* -------------------------------------------------------------------------- */
  // region resolveTab
  /** Valida el segmento de ruta contra las pestañas reales. Si viene vacío o
      con basura devuelve `undefined` y la página se queda con la bandeja por
      defecto en vez de romperse. */
  private resolveTab(
    value: unknown
  ): ReportTabKey | undefined {
    return this.tabs.find(
      tab => tab.key === value
    )?.key;
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR activeReports                           */
  /* -------------------------------------------------------------------------- */
  // region activeReports
  /** Reportes de la bandeja visible. */
  public get activeReports(): Report[] {
    return this.reportService.reports[this.activeTab] ?? [];
  }

  /** Pestaña visible, para leer su mensaje de bandeja vacía. */
  public get activeTabDefinition(): IReportTab {
    return this.tabs.find(
      tab => tab.key === this.activeTab
    ) ?? this.tabs[0];
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR counters                              */
  /* -------------------------------------------------------------------------- */
  // region counters
  /** Reportes de una bandeja concreta, para el contador de su pestaña. */
  public count(
    key: ReportTabKey
  ): number {
    return this.reportService.reports[key]?.length ?? 0;
  }

  /** Reportes sin cerrar: pendientes mas los que ya se están atendiendo. */
  public get openCount(): number {
    return this.count('pendings') + this.count('inProgress');
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR create                               */
  /* -------------------------------------------------------------------------- */
  // region create
  public create() {
    const modalRef = this.modalService.open(
      CreateReportModalComponent
    );

    modalRef.componentInstance.neighborhoodId = this.neighborhoodId;
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR refresh                               */
  /* -------------------------------------------------------------------------- */
  // region refresh
  public refresh() {
    this.reportService.getAll(
      this.neighborhoodId!
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR selectTab                              */
  /* -------------------------------------------------------------------------- */
  // region selectTab
  public selectTab(
    key: ReportTabKey
  ) {
    if (this.activeTab === key) return;

    this.activeTab = key;

    this.navigateToTab(key);
  }

  /* -------------------------------------------------------------------------- */
  /*                          ANCHOR openPendingReport                          */
  /* -------------------------------------------------------------------------- */
  // region openPendingReport
  /**
   * Abre el reporte que pidió un aviso de la campana, una vez que hay listas
   * contra las que buscarlo.
   *
   * Se llama desde dos lados —cuando llega el query param y cuando termina la
   * carga— porque el orden de esos dos eventos no está garantizado. Limpia
   * `pendingReportId` en cuanto decide actuar, así que la segunda llamada no
   * abre un segundo modal.
   *
   * No bloquea nada mientras tanto: la página sigue pintando sus bandejas con
   * su propio esqueleto y el modal aparece encima cuando hay con qué.
   */
  private async openPendingReport() {

    const reportId = this.pendingReportId;

    /* Sin colonia resuelta o con la petición en vuelo todavía no hay dónde
       buscar: el intento se repite cuando `getAll` termina. */
    if (!reportId || !this.neighborhoodId || this.reportService.loading) return;

    this.pendingReportId = undefined;

    const match = this.findReport(reportId);

    if (!match) {
      /* El aviso sobrevivió al reporte. Se dice, no se abre un modal vacío. */
      this.toastService.show(
        {
          header: 'Reporte no disponible',
          body: 'El reporte del aviso ya no está en la lista.',
          classname: 'bg-danger text-white'
        }
      );

      await this.navigateToTab(this.activeTab);
      return;
    }

    /* La URL termina apuntando a la bandeja real del reporte y sin el
       `reportId`: recargar o volver atrás no vuelve a abrir el modal. Se espera
       la navegación para que la pestaña ya esté puesta cuando aparezca el
       detalle. */
    await this.navigateToTab(match.key);

    const modalRef = this.modalService.open(
      ReportModalComponent
    );

    modalRef.componentInstance.report = match.report;

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR findReport                              */
  /* -------------------------------------------------------------------------- */
  // region findReport
  /** Busca un reporte en las tres bandejas y devuelve además en cuál estaba.
      Las listas ya cargadas son la única fuente que sabe eso: el aviso solo
      trae el id. */
  private findReport(
    reportId: string
  ): { key: ReportTabKey, report: Report } | undefined {

    for (const tab of this.tabs) {

      const report = (this.reportService.reports[tab.key] ?? []).find(
        report => report.reportId === reportId
      );

      if (report) {
        return {
          key: tab.key,
          report
        };
      }

    }

    return undefined;

  }

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR navigateToTab                            */
  /* -------------------------------------------------------------------------- */
  // region navigateToTab
  /** Reemplaza el segmento de la pestaña —`../` sube al `reports` padre— sin
      llenar el historial. Al no propagar query params, la misma navegación
      limpia el `reportId` que trajo el aviso. */
  private navigateToTab(
    key: ReportTabKey
  ): Promise<boolean> {

    return this.router.navigate(
      [
        '../',
        key
      ],
      {
        relativeTo: this.activatedRoute,
        replaceUrl: true
      }
    );

  }

}
